import {
  DryingProjectOverview,
  calculateDryingProgressPercent,
  DamagePatternConclusionState,
  getDamagePatternConclusionStateString,
  AppointmentInfo,
} from "./DryingProjectOverview";
import { isLeakDetectionProject as getIsLeakDetectionProject } from "@/data/repository/ProjectRepository";

import LeakDetectionProjectOverview from "./LeakDetectionProjectOverview";

import { isAppointmentStepInfoComplete } from "@/utils/ProjectStateFilter";

import {
  getEstimatedDryingTimeInDays,
  getProjectStepContainer,
  getStepCreationTimestamp,
  ProjectStepsContainer,
} from "@/data/models/ProjectSteps";

import {
  containsDryingInfo,
  RepairAction,
  containsLeakDetectionInfo,
  ProjectDetails,
  ProjectOperationSource,
  containsOnlyLeakDetectionInfo,
  getProjectTypeIdOfProject,
  doesProjectTypeIdHaveDrying,
  doesProjectTypeIdHaveLeakDetection,
  doesProjectTypeIdHaveDamageAssessment,
  doesProjectTypeIdHaveRestoration,
  isLeakDetectionOnlyProjectTypeId,
} from "./ProjectDetails";
import ProjectOverview, {
  isProjectOverviewWebsiteOnly,
  isProjectFinished,
} from "./ProjectOverview";
import { getNumberDaysBetween } from "@/utils/DateHelper";
import { UserLookup } from "./UserLookup";
import {
  COLOR_APPOINTMENT_DAMAGE_ASSESSMENT,
  COLOR_APPOINTMENT_DEFAULT,
  COLOR_APPOINTMENT_DRYING_CONCLUSION,
  COLOR_APPOINTMENT_DRYING_PREPARATION,
  COLOR_APPOINTMENT_FOLLOW_UP,
  COLOR_APPOINTMENT_INTERMEDIATE_MEASUREMENT,
  COLOR_APPOINTMENT_LEAK_DETECTION,
  COLOR_APPOINTMENT_REPAIR,
  COLOR_PROJECT_TYPE_STATE_01,
  COLOR_PROJECT_TYPE_STATE_02,
  COLOR_PROJECT_TYPE_STATE_03,
  COLOR_PROJECT_TYPE_STATE_04,
  COLOR_PROJECT_TYPE_STATE_05,
  COLOR_PROJECT_TYPE_STATE_06,
  COLOR_PROJECT_TYPE_STATE_07,
  COLOR_PROJECT_TYPE_STATE_08,
  COLOR_PROJECT_TYPE_STATE_09,
} from "@/utils/colors";
import { 
  dateTimeFromIsoString, 
  formatDateShort, 
  formatJsDate 
} from "@/utils/DateFormatter";
import { joinToString } from "@/utils/LocalisationHelper";
import { UserModule } from "@/store/modules/UserModule";
import { RecordInfo, RecordInfoFlex } from "./RecordInfo";
import { formatAppointmentInfo } from "./ProjectLog";
import { filterOutUndefined, nullIfBlank } from "@/utils/Utils";

export enum ProjectStatusSyncStatus {
  NEEDS_SYNC = "NEEDS_SYNC",
  SYNCED = "SYNCED",
  RETRY_SYNC_LATER = "RETRY_SYNC_LATER",
  PROJECT_ALREADY_FINISHED = "PROJECT_ALREADY_FINISHED",
  APPOINTMENT_ALREADY_COMPLETED = "APPOINTMENT_ALREADY_COMPLETED",
}

export enum ProjectPhase {
  LeakDetection,
  DamageAssessment,
  Drying,
}

export enum AppointmentType {
  LEAK_DETECTION,
  DAMAGE_ASSESSMENT,
  PREPARE_DRYING,
  INTERMEDIATE_MEASUREMENT,
  DRYING_CONCLUSION,
  REPAIR,
  FOLLOW_UP_INSPECTION,
}

export enum ProjectStateType {
  ApproveProject = "ApproveProject",
  ProjectApprovalDone = "ProjectApprovalDone",
  FirstContactDone = "FirstContactDone",

  LeakDetectionRunning = "LeakDetectionRunning",
  LeakDetectionFinished = "LeakDetectionFinished",

  DamageAssessmentRunning = "DamageAssessmentRunning",
  DamageAssessmentDone = "DamageAssessmentDone",

  DryingApproved = "DryingApproved",

  InsuranceOfferSend = "InsuranceOfferSend",
  InsuranceOfferResult = "InsuranceOfferResult",

  DryingPreperationRunning = "DryingPreperationRunning",
  DryingStarted = "DryingStarted",

  DryingResult = "DryingResult",

  Appointment = "Appointment",
  RepairStarted = "RepairStarted",
  RepairCompleted = "RepairCompleted",
  ProjectFinished = "ProjectFinished",

  AssignDevices = "AssignDevices",
  SetProjectSupervisor = "SetProjectSupervisor",
  ConcludeProject = "ConcludeProject",
  PhaseSuspended = "PhaseSuspended",
  PhaseResumed = "PhaseResumed",
}

export enum ProjecStateForProjectTypeDisplay {
  DryingConclusionRunning = "DryingConclusionRunning",
  IntermediateMeasurementRunning = "IntermediateMeasurementRunning",
  IntermediateMeasurementDone = "IntermediateMeasurementDone"
}

interface ProjectStepInfo {
  createdBy: string;
  creationTimestamp: string;
  source?: ProjectOperationSource;
  appointmentCreationTimestamp?: string;
  appointmentCreatedBy?: string;
}

interface ProjectReportInfo extends ProjectStepInfo {
  downloadRefInternalPdfReport?: string;
  downloadRefExternalPdfReport?: string;
  downloadRefJsonData?: string;
}

interface StepDescription {
  stepCreatedBy: RecordInfo;
  stepDescription: string;
  stepSource?: ProjectOperationSource;
}

interface DryingConclusionResult {
  createdBy: string;
  creationTimestamp: string;
  dryingSuccessful: number;
  note: string;
  contactPersonNumber: number;
  downloadRefInternalPdfReport?: string | null;
}

interface ProjectResultStep extends ProjectReportInfo {
  createdBy: string;
  creationTimestamp: string;
  downloadRefServicesCsv?: string | null;
}

export function getAppointmentDescriptionForProjectEventLog(
  vue: Vue,
  appointmentType: AppointmentType,
  isPortalOnlyProject?: boolean,
): string {
  switch (appointmentType) {
    case AppointmentType.LEAK_DETECTION: {
      return vue.$t("projectLogs.eventLogs.appointmentLeakDetection").toString();
    }
    case AppointmentType.DAMAGE_ASSESSMENT: {
      return vue.$t("projectLogs.eventLogs.appointmentDamageAssessment").toString();
    }
    case AppointmentType.PREPARE_DRYING: {
      return vue.$t(`projectLogs.eventLogs.appointmentDryingPreparation${isPortalOnlyProject ? 'PortalProject' : ''}`).toString();
    }
    case AppointmentType.DRYING_CONCLUSION: {
      return vue.$t(`projectLogs.eventLogs.appointmentDryingConclusion${isPortalOnlyProject ? 'PortalProject' : ''}`).toString();
    }
    case AppointmentType.INTERMEDIATE_MEASUREMENT: {
      return vue.$t("projectLogs.eventLogs.appointmentIntermediate").toString();
    }
    case AppointmentType.REPAIR: {
      return vue.$t("projectLogs.eventLogs.appointmentRepair").toString();
    }
    case AppointmentType.FOLLOW_UP_INSPECTION: {
      return vue.$t("projectLogs.eventLogs.appointmentFollowUpInspection").toString();
    }
  }
}

export function getAppointmentTypeDescription(
  vue: Vue,
  appointmentType: AppointmentType
): string {
  switch (appointmentType) {
    case AppointmentType.LEAK_DETECTION: {
      return vue.$t("appointmentType.leakDetection").toString();
    }
    case AppointmentType.DAMAGE_ASSESSMENT: {
      return vue.$t("appointmentType.damageAssessment").toString();
    }
    case AppointmentType.PREPARE_DRYING: {
      return vue.$t("appointmentType.dryingSetup").toString();
    }
    case AppointmentType.DRYING_CONCLUSION: {
      return vue.$t("appointmentType.dryingConclusion").toString();
    }
    case AppointmentType.INTERMEDIATE_MEASUREMENT: {
      return vue.$t("appointmentType.measurement").toString();
    }
    case AppointmentType.REPAIR: {
      return vue.$t("appointmentType.repair").toString();
    }
    case AppointmentType.FOLLOW_UP_INSPECTION: {
      return vue.$t("appointmentType.followUpInspection").toString();
    }
  }
}

export interface DefaultProjectStepInfo {
  projectStepType: ProjectStateType;
}

export type SuspendedProjectStepResourceKey =
  | "leakDetectionSuspended"
  | "damageAssessmentSuspended"
  | "dryingSetupSuspended"
  | "intermediateMeasurementSuspended"
  | "dryingPackupSuspended"
  | "repairSuspended";

export type ResumedProjectStepResourceKey =
  | "leakDetectionResumed"
  | "damageAssessmentResumed"
  | "dryingSetupResumed"
  | "intermediateMeasurementResumed"
  | "dryingPackupResumed"
  | "repairResumed";

export interface SuspendedProjectStepInfo extends DefaultProjectStepInfo {
  projectStepType: ProjectStateType.PhaseSuspended;
  suspendedResourceKey: SuspendedProjectStepResourceKey;
}

export interface ResumedProjectStepInfo extends DefaultProjectStepInfo {
  projectStepType: ProjectStateType.PhaseResumed;
  resumedResourceKey: ResumedProjectStepResourceKey;
}

export interface ExtendedDefaultProjectStepInfo {
  projectStepType: ProjectStateType | ProjecStateForProjectTypeDisplay;
  appointmentType?: AppointmentType;
}

export interface ReportInfo {
  hasReport: boolean;
}

export interface ProjectApprovalDoneInfo {
  projectStepType: ProjectStateType;
  wasApproved: boolean;
}

export interface InsuranceOfferResultInfo {
  projectStepType: ProjectStateType;
  dryingApproved: boolean;
  projectFinished: boolean;
}

export interface DryingDurationStepInfo {
  projectStepType: ProjectStateType;
  estimatedDryingDurationInDays?: number;
  isDryingEndOverdue: boolean;
  hasProjectSupervisor: boolean;
  numDryingDays?: number;
}

export interface AppointmentStepInfo {
  projectStepType: ProjectStateType;
  appointmentType: AppointmentType;
  appointmentDate: Date | null;
  responsibleUser?: string;
  /** Additional (team) users assisting the main responsibleUser. Raw identifiers. */
  additionalUsers?: string[];
  contactPersonNumber: number;
}

export interface ProjectFinishedInfo extends ReportInfo {
  projectPhase: ProjectPhase;
  projectStepType: ProjectStateType;
}

export interface AppointmentForContactPersonInfo extends AppointmentInfo {
  contactPersonNumber: number;
}

export interface DamageAssessmentDoneInfo extends ReportInfo {
  projectStepType: ProjectStateType;
  isProjectOnHold: boolean;
  noDryingNecessary: boolean;
}

export interface DamageAssessmentResultStep {
  createdBy: string;
  creationTimestamp: string;
  downloadRefInternalPdfReport?: string | null;
  downloadRefExternalPdfReport?: string | null;
  downloadRefJsonData?: string | null;
  downloadRefServicesCsv?: string | null;
  approvalStatus: number;
  estimatedDryingTime?: number;
  damagePatternConclusionState?: DamagePatternConclusionState | null;
}

export interface DryingResultInfo extends ReportInfo {
  projectStepType: ProjectStateType;
  repairAction: RepairAction | undefined;
}

export interface LeakDetectionDoneInfo extends ReportInfo {
  projectStepType: ProjectStateType;
}

function getProjectApprovalInfo(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  step: any,
  projectOverview: ProjectOverview
): ProjectApprovalDoneInfo {
  return {
    projectStepType: isProjectOverviewWebsiteOnly(projectOverview)
      ? ProjectStateType.SetProjectSupervisor
      : ProjectStateType.ProjectApprovalDone,
    wasApproved: step.orderAccepted !== 0,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getLeakDetectionDoneInfo(step: any): LeakDetectionDoneInfo {
  return {
    projectStepType: ProjectStateType.LeakDetectionFinished,
    hasReport: !!step.downloadRefExternalPdfReport,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getDamageAssessmentDoneInfo(step: any): DamageAssessmentDoneInfo {
  return {
    projectStepType: ProjectStateType.DamageAssessmentDone,
    hasReport: !!step.downloadRefExternalPdfReport,
    isProjectOnHold: step.approvalStatus === 1,
    noDryingNecessary: step.approvalStatus === 0,
  };
}

function getInsuranceOfferResultInfo(
  projectOverview: DryingProjectOverview,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  step: any
): InsuranceOfferResultInfo {
  return {
    projectStepType: ProjectStateType.InsuranceOfferResult,
    dryingApproved: step.approvalStatus === 2,
    projectFinished:
      projectOverview.damagePatternConclusionState !==
      DamagePatternConclusionState.CARRY_OUT_DRYING,
  };
}

function getProjectFinishedInfo(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  step: any,
  projectPhase: ProjectPhase
): ProjectFinishedInfo {
  return {
    projectPhase: projectPhase,
    projectStepType: ProjectStateType.ProjectFinished,
    hasReport: step && !!step.downloadRefExternalPdfReport,
  };
}

export type ProjectStateInfo =
  | DefaultProjectStepInfo
  | ProjectApprovalDoneInfo
  | SuspendedProjectStepInfo
  | ResumedProjectStepInfo;
export type ExtendedProjectStateInfo =  ExtendedDefaultProjectStepInfo;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isNullOrEmpty(data: any) {
  if (!data) {
    return true;
  }

  if (Array.isArray(data)) {
    return data.length == 0;
  } else {
    return false;
  }
}

export function getNextAppointment(
  appointments: AppointmentForContactPersonInfo[] | undefined,
  today: Date,
  appointmentType: AppointmentType
): AppointmentStepInfo | null {
  if (!appointments || !Array.isArray(appointments)) {
    return null;
  }

  const dateNotSetValue = Number.MAX_SAFE_INTEGER;

  const sortedAppointments = appointments
    .filter((appointment) => !appointment.completed)
    .map((appointment) => {
      const appointmentDate = appointment.appointmentDate;

      const time = appointmentDate
        ? new Date(appointmentDate).getTime()
        : dateNotSetValue;

      return {
        appointment: appointment,
        timestamp: time,
      };
    })
    .sort((a, b) => a.timestamp - b.timestamp);

  if (sortedAppointments.length === 0) {
    return null;
  }

  const nextAppointment =
    sortedAppointments.find((a) => a.timestamp >= today.getTime()) ??
    sortedAppointments[0];

  if (nextAppointment) {
    return {
      appointmentType: appointmentType,
      projectStepType: ProjectStateType.Appointment,
      appointmentDate:
        nextAppointment.timestamp === dateNotSetValue
          ? null
          : new Date(nextAppointment.timestamp),
      contactPersonNumber: nextAppointment.appointment.contactPersonNumber,
      responsibleUser: nextAppointment.appointment.responsibleUser,
    };
  } else {
    return null;
  }
}

export function getNextDryingOrRestorationAppointment(
  dryingProjectOverview: DryingProjectOverview,
  today: Date,
  hasDrying = true
): AppointmentStepInfo | null {
  const nextAppointments = [
    ...(hasDrying
      ? [
          getNextAppointment(
            dryingProjectOverview.dryingProjectSteps?.dryingSetupAppointments,
            today,
            AppointmentType.PREPARE_DRYING
          ),
          getNextAppointment(
            dryingProjectOverview.dryingProjectSteps
              ?.intermediateMeasurementAppointments,
            today,
            AppointmentType.INTERMEDIATE_MEASUREMENT
          ),
          getNextAppointment(
            dryingProjectOverview.dryingProjectSteps
              ?.dryingConclusionAppointments,
            today,
            AppointmentType.DRYING_CONCLUSION
          ),
        ]
      : []),
    getNextAppointment(
      dryingProjectOverview.dryingProjectSteps?.repairAppointments,
      today,
      AppointmentType.REPAIR
    ),
  ];

  if (dryingProjectOverview.followUpInspectionDate) {
    nextAppointments.push({
      appointmentType: AppointmentType.FOLLOW_UP_INSPECTION,
      appointmentDate: dryingProjectOverview.followUpInspectionDate.toDate(),
      contactPersonNumber: 0,
      projectStepType: ProjectStateType.Appointment,
    });
  }

  return (
    nextAppointments
      .filter((appointment) => !!appointment)
      .sort(
        (a, b) =>
          (a?.appointmentDate?.getTime() ?? Number.MAX_SAFE_INTEGER) -
          (b?.appointmentDate?.getTime() ?? Number.MAX_SAFE_INTEGER)
      )[0] ?? null
  );
}

export function hasValue(value: boolean | undefined | null) {
  return value !== undefined && value !== null;
}

function getDryingResultInfo(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  projectStep: any,
  projectOverview: DryingProjectOverview
): DryingResultInfo {
  return {
    projectStepType: ProjectStateType.DryingResult,
    repairAction: projectOverview.repairAction,
    hasReport: !!projectStep.downloadRefExternalPdfReport,
  };
}

function getDryingStartedInfo(
  projectStepType: ProjectStateType,
  estimatedDryingDurationInDays: number | undefined,
  projectOverview: DryingProjectOverview
): DryingDurationStepInfo {
  let isDryingEndOverdue = false;

  if (estimatedDryingDurationInDays) {
    isDryingEndOverdue = calculateDryingProgressPercent(projectOverview) >= 1;
  }

  let numDryingDays: undefined | number = undefined;
  if (projectOverview.earliestIresDeviceAssignmentStartDate) {
    const now = new Date();

    numDryingDays = getNumberDaysBetween(
      now,
      projectOverview.earliestIresDeviceAssignmentStartDate.toDate()
    );
  }

  return {
    numDryingDays: numDryingDays,
    projectStepType: projectStepType,
    estimatedDryingDurationInDays: estimatedDryingDurationInDays,
    isDryingEndOverdue: isDryingEndOverdue,
    hasProjectSupervisor: !!projectOverview.projectSupervisorId,
  };
}

/**
 * Millis of a project-step string timestamp (`creationTimestamp`, ISO-8601), or `null`
 * when it is absent, empty or unparseable. Cloud seeds result entries with
 * `creationTimestamp: ""`, so "no usable timestamp" is a normal case, not a data error.
 */
function getStepTimestampMillis(timestamp: unknown): number | null {
  if (typeof timestamp !== "string" || timestamp.trim() === "") {
    return null;
  }

  const millis = dateTimeFromIsoString(timestamp)?.getTime();

  return typeof millis === "number" && !isNaN(millis) ? millis : null;
}

/**
 * Millis of the suspension recorded on a step entry, or `null` when it carries none or
 * the timestamp is unusable. Undated suspensions rank as the OLDEST when candidates are
 * compared (see {@link getLifecyclePhaseState}).
 */
function getStepSuspensionMillis(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  projectStep: any
): number | null {
  return getStepTimestampMillis(
    projectStep?.phaseSuspended?.suspendedBy?.creationTimestamp
  );
}

/**
 * Millis of the resume recorded on a step entry, or `null` when it carries none or the
 * timestamp is unusable. Undated resumes rank as the OLDEST, like undated suspensions.
 */
function getStepResumptionMillis(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  projectStep: any
): number | null {
  return getStepTimestampMillis(
    projectStep?.phaseSuspended?.resumedBy?.creationTimestamp
  );
}

/**
 * True when the `phaseSuspended` marker describes a suspension that is still in force.
 *
 * A suspension counts as active when it has a `suspendedBy` and either no `resumedBy`,
 * or a `resumedBy` that provably PREDATES it. We must not infer "still suspended" from
 * the mere absence of `resumedBy`: suspending after a resume is a normal sequence (a
 * concluded phase reopened, worked on, then paused again), and while every writer we
 * know of clears `resumedBy` on suspend (IRES Work's `SuspendCurrentStep`, Cloud's
 * auto-close writes `resumedBy: null`), a reader that depends on that is one careless
 * write away from reporting a paused phase as running.
 *
 * When a timestamp is missing on either side the resume wins, because it cannot be shown
 * to be the older event — the same "undated ranks oldest" rule used for ordering.
 */
export function isPhaseSuspensionActive(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  phaseSuspended: any
): boolean {
  if (!phaseSuspended?.suspendedBy) {
    return false;
  }

  if (!phaseSuspended.resumedBy) {
    return true;
  }

  const suspendedAt = getStepTimestampMillis(
    phaseSuspended.suspendedBy.creationTimestamp
  );
  const resumedAt = getStepTimestampMillis(
    phaseSuspended.resumedBy.creationTimestamp
  );

  if (suspendedAt === null) {
    return false;
  }

  return resumedAt === null || resumedAt < suspendedAt;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getIsStepResumed(projectStep: any, phaseReOpened?: boolean) {
  if (!projectStep) {
    return false;
  }

  const wasStepCompleted = projectStep && projectStep.createdBy !== "";
  const showResumedInfo = !wasStepCompleted || phaseReOpened;
  const isStepResumed = showResumedInfo &&
    projectStep.phaseSuspended && 
    projectStep.phaseSuspended.resumedBy &&
    !isPhaseSuspensionActive(projectStep.phaseSuspended);

  return isStepResumed;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getIsStepSuspended(projectStep: any, phaseReOpened?: boolean) {
  if (!projectStep) {
    return false;
  }

  const wasStepCompleted = projectStep && projectStep.createdBy !== "";
  const showSuspendedInfo = !wasStepCompleted || phaseReOpened;
  const isStepSuspended = showSuspendedInfo &&
    isPhaseSuspensionActive(projectStep.phaseSuspended);

  return isStepSuspended;
}

function getSuspendedProjectState(
  suspendedResourceKey: SuspendedProjectStepResourceKey
): SuspendedProjectStepInfo {
  return {
    projectStepType: ProjectStateType.PhaseSuspended,
    suspendedResourceKey,
  };
}

function getResumedProjectState(
  resumedResourceKey: ResumedProjectStepResourceKey
): ResumedProjectStepInfo {
  return {
    projectStepType: ProjectStateType.PhaseResumed,
    resumedResourceKey,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getLifecycleProjectState(projectStep: any, suspendedResourceKey: SuspendedProjectStepResourceKey, resumedResourceKey: ResumedProjectStepResourceKey): SuspendedProjectStepInfo | ResumedProjectStepInfo | null {
  if (!projectStep) {
    return null;
  }

  if (getIsStepSuspended(projectStep, projectStep?.phaseReOpened)) {
    return getSuspendedProjectState(suspendedResourceKey);
  }

  if (getIsStepResumed(projectStep, projectStep?.phaseReOpened)) {
    return getResumedProjectState(resumedResourceKey);
  }

  return null;
}

/** One phase with a lifecycle (suspend/resume) marker: where its entries live, how to label it. */
interface LifecyclePhase {
  /** Project-status step number. Lower = earlier in the project's lifecycle. */
  stepIndex: number;
  suspendedResourceKey: SuspendedProjectStepResourceKey;
  resumedResourceKey: ResumedProjectStepResourceKey;
  /** Result entry (steps 5/7) or list of contact-person entries (steps 11/13/15/17). */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getEntries: (projectSteps: any) => any;
}

/** Every phase that can carry a `phaseSuspended` marker, in ascending step order. */
const LIFECYCLE_PHASES: LifecyclePhase[] = [
  {
    stepIndex: 5,
    suspendedResourceKey: "leakDetectionSuspended",
    resumedResourceKey: "leakDetectionResumed",
    getEntries: (projectSteps) => projectSteps?.leakDetectionResult,
  },
  {
    stepIndex: 7,
    suspendedResourceKey: "damageAssessmentSuspended",
    resumedResourceKey: "damageAssessmentResumed",
    getEntries: (projectSteps) => projectSteps?.damageAssessmentResult,
  },
  {
    stepIndex: 11,
    suspendedResourceKey: "dryingSetupSuspended",
    resumedResourceKey: "dryingSetupResumed",
    getEntries: (projectSteps) => projectSteps?.dryingSetupCompletions,
  },
  {
    stepIndex: 13,
    suspendedResourceKey: "intermediateMeasurementSuspended",
    resumedResourceKey: "intermediateMeasurementResumed",
    getEntries: (projectSteps) => projectSteps?.intermediateMeasurementCompletions,
  },
  {
    stepIndex: 15,
    suspendedResourceKey: "dryingPackupSuspended",
    resumedResourceKey: "dryingPackupResumed",
    getEntries: (projectSteps) => projectSteps?.dryingConclusionResults,
  },
  {
    stepIndex: 17,
    suspendedResourceKey: "repairSuspended",
    resumedResourceKey: "repairResumed",
    getEntries: (projectSteps) => projectSteps?.repairStart?.extendedRepairResult,
  },
];

/** A phase entry currently suspended or resumed, and when that happened. */
interface LifecyclePhaseCandidate {
  stepIndex: number;
  isSuspended: boolean;
  suspendedResourceKey: SuspendedProjectStepResourceKey;
  resumedResourceKey: ResumedProjectStepResourceKey;
  /** `null` when the marker carries no usable timestamp; ranks as the oldest. */
  atMillis: number | null;
}

/** True when `candidate` describes a more recent lifecycle event than `best`. */
function isMoreCurrentLifecycleEvent(
  candidate: LifecyclePhaseCandidate,
  best: LifecyclePhaseCandidate | null
): boolean {
  if (!best) {
    return true;
  }

  if (candidate.atMillis !== best.atMillis) {
    // An undated event ranks as the oldest, so a dated one always beats it.
    if (candidate.atMillis === null) {
      return false;
    }
    if (best.atMillis === null) {
      return true;
    }

    return candidate.atMillis > best.atMillis;
  }

  return candidate.stepIndex < best.stepIndex;
}

/**
 * Returns the phase lifecycle state the project is currently in — `PhaseSuspended` for a
 * paused phase, `PhaseResumed` for one picked back up — or `null` when no phase carries a
 * live lifecycle marker.
 *
 * A suspended phase reflects the project's true current status, so it must take
 * precedence over a scheduled next appointment (e.g. a follow-up inspection).
 * Otherwise a paused project is mislabelled — for example the overview would show
 * "Trocknung aktiv" (from the pending follow-up appointment) instead of
 * "Messung unterbrochen" for a suspended intermediate measurement.
 *
 * Several markers can be live at once — across phases (a concluded phase gets reopened
 * and paused again while a later one is still paused) and within one phase (a
 * contact-person phase carries an entry per contact person). Suspends and resumes
 * therefore compete in ONE pool over every entry of every phase, and the winner is:
 *
 *   1. the LATEST event by `suspendedBy`/`resumedBy` `creationTimestamp`. Resumes must
 *      compete on time too: a leak detection resumed AFTER a drying setup was paused is
 *      the newer fact about the project, so it reads "Leckortung fortgesetzt" and not
 *      "Aufbau unterbrochen";
 *   2. an event with no usable timestamp ranks as the oldest;
 *   3. on a tie, the EARLIEST step — reopening walks backwards (reopen 15, then 13,
 *      then 11 …), so the lower step is where the crew actually is.
 */
function getLifecyclePhaseState(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  projectSteps: any
): SuspendedProjectStepInfo | ResumedProjectStepInfo | null {
  let best: LifecyclePhaseCandidate | null = null;

  for (const phase of LIFECYCLE_PHASES) {
    const entries = phase.getEntries(projectSteps);
    // Steps 5 and 7 hold a single result object; the contact-person phases hold a list.
    const entryList = Array.isArray(entries) ? entries : [entries];

    for (const entry of entryList) {
      const isSuspended = !!getIsStepSuspended(entry, entry?.phaseReOpened);
      const isResumed = !isSuspended && !!getIsStepResumed(entry, entry?.phaseReOpened);

      if (!isSuspended && !isResumed) {
        continue;
      }

      const candidate: LifecyclePhaseCandidate = {
        stepIndex: phase.stepIndex,
        isSuspended,
        suspendedResourceKey: phase.suspendedResourceKey,
        resumedResourceKey: phase.resumedResourceKey,
        atMillis: isSuspended
          ? getStepSuspensionMillis(entry)
          : getStepResumptionMillis(entry),
      };

      if (isMoreCurrentLifecycleEvent(candidate, best)) {
        best = candidate;
      }
    }
  }

  if (!best) {
    return null;
  }

  return best.isSuspended
    ? getSuspendedProjectState(best.suspendedResourceKey)
    : getResumedProjectState(best.resumedResourceKey);
}

export function getLatestDryingProjectState(
  projectOverview: DryingProjectOverview,
  today: Date
): ProjectStateInfo | null {
  const hasDrying = containsDryingInfo(projectOverview);
  const projectSteps = projectOverview.dryingProjectSteps;

  if (!projectSteps) {
    return null;
  }

  // The most recent phase lifecycle event (suspend or resume, across ALL phases) is the
  // project's true current status, so it is resolved before any other state — see
  // `getLifecyclePhaseState`. The one exception is a finished project: a concluded project
  // keeps its finished label even if a reopened phase still carries a lifecycle marker.
  // This also supersedes the per-phase lifecycle checks further down, which can no longer
  // fire once this returned nothing.
  if (!projectSteps.projectResult) {
    const lifecyclePhaseState = getLifecyclePhaseState(projectSteps);

    if (lifecyclePhaseState) {
      return lifecyclePhaseState;
    }
  }

  const leakDetectionResult = projectSteps.leakDetectionResult;

  if (containsOnlyLeakDetectionInfo(projectOverview) && leakDetectionResult) {
    return getProjectFinishedInfo(
      leakDetectionResult,
      ProjectPhase.LeakDetection
    );
  }

  const damageAssessmentResult = projectSteps.damageAssessmentResult;

  const estimatedDryingDurationInDays =
    projectSteps.offerResult?.estimatedDryingTime ??
    damageAssessmentResult?.estimatedDryingTime;

  const shouldCarryOutDryingOrRestoration = projectOverview.damagePatternConclusionComplete == true &&
    projectOverview.damagePatternConclusionState ==
      DamagePatternConclusionState.CARRY_OUT_DRYING;

  const hasDryingSetupAppointments = hasDrying && (
      !isNullOrEmpty(projectSteps.dryingSetupAppointments) ||
      projectOverview.setupDryingStartedBy != null
  );

  const hasRestorationAppointments = !hasDrying && (
      !isNullOrEmpty(projectSteps.repairAppointments) ||
      projectOverview.repairAction != null
  );

  const wasDryingOrRestorationStarted = shouldCarryOutDryingOrRestoration &&
    (
      hasDryingSetupAppointments ||
      hasRestorationAppointments
    );

  if (wasDryingOrRestorationStarted) {
    const projectResult = projectSteps.projectResult;

    if (projectResult) {
      return getProjectFinishedInfo(projectResult, ProjectPhase.Drying);
    }

    const nextAppointment = getNextDryingOrRestorationAppointment(projectOverview, today, hasDrying);

    if (nextAppointment) {
      return nextAppointment;
    }

    const repairResults = projectSteps.repairStart?.extendedRepairResult;
    if (Array.isArray(repairResults) && repairResults.length > 0) {
      const lastRepairResult = repairResults[repairResults.length - 1];
      const repairLifecycleState = getLifecycleProjectState(
        lastRepairResult,
        "repairSuspended",
        "repairResumed"
      );
      if (repairLifecycleState) {
        return repairLifecycleState;
      }
    }
    if (
      projectOverview.repairFinishedBy &&
      Array.isArray(repairResults) &&
      repairResults.length >= 0
    ) {
      const latestRepairResult =
        repairResults[repairResults.length - 1];

      if (latestRepairResult) {
        return {
          projectStepType: ProjectStateType.RepairCompleted,
        };
      }
    }
    else if (projectOverview.repairCompletedBy) {
      return {
        projectStepType: ProjectStateType.RepairCompleted,
      };
    }

    if (projectSteps.repairAppointments?.some((appointment) => appointment.completed)) {
      return {
        projectStepType: ProjectStateType.RepairStarted,
      };
    }

    const dryingConclusionResults = projectSteps.dryingConclusionResults;

    if (Array.isArray(dryingConclusionResults) && dryingConclusionResults.length > 0) {
      const lastConclusionResult = dryingConclusionResults[dryingConclusionResults.length - 1];
      const dryingPackupLifecycleState = getLifecycleProjectState(
        lastConclusionResult,
        "dryingPackupSuspended",
        "dryingPackupResumed"
      );
      if (dryingPackupLifecycleState) {
        return dryingPackupLifecycleState;
      }
    }

    if (
      projectOverview.packupDevicesFinishedBy &&
      Array.isArray(dryingConclusionResults) &&
      dryingConclusionResults.length >= 0
    ) {
      const latestDryingConclusionResult =
        dryingConclusionResults[dryingConclusionResults.length - 1];

      if (latestDryingConclusionResult) {
        return getDryingResultInfo(
          latestDryingConclusionResult,
          projectOverview
        );
      }
    }

    const intermediateMeasurementCompletions = projectSteps.intermediateMeasurementCompletions;
    if (Array.isArray(intermediateMeasurementCompletions) && intermediateMeasurementCompletions.length > 0) {
      const lastMeasurement = intermediateMeasurementCompletions[intermediateMeasurementCompletions.length - 1];
      const intermediateMeasurementLifecycleState = getLifecycleProjectState(
        lastMeasurement,
        "intermediateMeasurementSuspended",
        "intermediateMeasurementResumed"
      );
      if (intermediateMeasurementLifecycleState) {
        return intermediateMeasurementLifecycleState;
      }
    }

    const dryingSetupCompletionsList = projectSteps.dryingSetupCompletions;
    if (Array.isArray(dryingSetupCompletionsList) && dryingSetupCompletionsList.length > 0) {
      const lastSetupCompletion = dryingSetupCompletionsList[dryingSetupCompletionsList.length - 1];
      const dryingSetupLifecycleState = getLifecycleProjectState(
        lastSetupCompletion,
        "dryingSetupSuspended",
        "dryingSetupResumed"
      );
      if (dryingSetupLifecycleState) {
        return dryingSetupLifecycleState;
      }
    }

    if (
      projectOverview.dryingSetupComplete == true ||
      projectSteps.dryingSetupCompletions
    ) {
      return getDryingStartedInfo(
        ProjectStateType.DryingStarted,
        estimatedDryingDurationInDays,
        projectOverview
      );
    }

    if (projectOverview.setupDryingStartedBy) {
      return getDryingStartedInfo(
        ProjectStateType.DryingPreperationRunning,
        estimatedDryingDurationInDays,
        projectOverview
      );
    }

    return null;
  }

  if (
    projectOverview.damagePatternConclusionComplete === true &&
    projectOverview.damagePatternConclusionState ===
      DamagePatternConclusionState.CARRY_OUT_DRYING
  ) {
    return {
      projectStepType: ProjectStateType.DryingApproved,
    };
  }

  if (
    !projectSteps.offerResult &&
    damageAssessmentResult?.approvalStatus == 2
  ) {
    return {
      projectStepType: ProjectStateType.DryingApproved,
    };
  }

  const offerResult = projectSteps.offerResult;

  if (
    offerResult &&
    !(
      offerResult.approvalStatus == 0 &&
      damageAssessmentResult?.approvalStatus == 2
    )
  ) {
    return getInsuranceOfferResultInfo(projectOverview, offerResult);
  }

  if (projectSteps.offerSendToInsurance) {
    return {
      projectStepType: ProjectStateType.InsuranceOfferSend,
    };
  }

  if (damageAssessmentResult) {
    return getDamageAssessmentDoneInfo(damageAssessmentResult);
  }

  if (
    hasValue(projectOverview.damagePatternComplete) ||
    hasValue(projectOverview.documentationAndLeakDetectionComplete) ||
    hasValue(projectOverview.damagePatternConclusionComplete)
  ) {
    if (containsLeakDetectionInfo(projectOverview)) {
      if (leakDetectionResult) {
        if (projectSteps.damageAssessmentAppointment) {
          return {
            projectStepType: ProjectStateType.DamageAssessmentRunning,
          };
        }
        else {
          return {
            projectStepType: ProjectStateType.LeakDetectionFinished,
          };
        }
        
      } else {
        return {
          projectStepType: ProjectStateType.LeakDetectionRunning,
        };
      }
    } else {
      return {
        projectStepType: ProjectStateType.DamageAssessmentRunning,
      };
    }
  }

  if (leakDetectionResult) {
    return getLeakDetectionDoneInfo(leakDetectionResult);
  }

  return null;
}

/**
 * GZ tile previews were released on 14.05.2026. Projects started before that
 * date do not have the required step-complete flags — use the simpler legacy
 * state calculation for them.
 */
const GZ_TILES_CUTOFF = new Date(2026, 4, 14); // 14 May 2026

export function getLatestProjectStateOfWebsiteOnlyProject(
  projectOverview: ProjectOverview,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  steps: any,
  today: Date
): ProjectStateInfo | null {
  if (steps.projectApproved && steps.projectApproved.orderAccepted === 0) {
    //Project was rejected
    return getProjectApprovalInfo(steps.projectApproved, projectOverview);
  }

  if (isProjectFinished(projectOverview)) {
    return getProjectFinishedInfo(steps.projectResult, ProjectPhase.Drying);
  }

  if (projectOverview.allAssignmentsClosed) {
    return {
      projectStepType: ProjectStateType.ConcludeProject,
    };
  }

  const dryingOverview = projectOverview as DryingProjectOverview;
  const projectSteps = dryingOverview.dryingProjectSteps;
  const isNewGzProject = projectOverview.startDate?.toDate() >= GZ_TILES_CUTOFF;

  // The latest phase lifecycle event reflects the true current status and must take
  // precedence over the drying sub-state / next-appointment logic below (e.g. a
  // follow-up inspection), so a paused GZ project is not mislabelled "Trocknung aktiv".
  // A finished project already returned above, so no finished-state guard is needed here.
  const lifecyclePhaseState = getLifecyclePhaseState(projectSteps);
  if (lifecyclePhaseState) {
    return lifecyclePhaseState;
  }

  if (projectOverview.allAssignmentsClosed === false) {
    // Legacy path for older GZ projects — simple DryingStarted without sub-states
    if (!isNewGzProject) {
      return getDryingStartedInfo(
        ProjectStateType.DryingStarted,
        undefined,
        projectOverview as DryingProjectOverview
      );
    }

    const estimatedDryingDurationInDays =
      dryingOverview.estimatedDryingDurationInDays ??
      projectSteps?.offerResult?.estimatedDryingTime ??
      projectSteps?.damageAssessmentResult?.estimatedDryingTime;

    const nextAppointment = getNextDryingOrRestorationAppointment(dryingOverview, today, true);
    if (nextAppointment) {
      return nextAppointment;
    }

    const dryingConclusionResults = projectSteps?.dryingConclusionResults;
    if (Array.isArray(dryingConclusionResults) && dryingConclusionResults.length > 0) {
      const latestDryingConclusionResult = dryingConclusionResults[dryingConclusionResults.length - 1];
      const dryingPackupLifecycleState = getLifecycleProjectState(
        latestDryingConclusionResult,
        "dryingPackupSuspended",
        "dryingPackupResumed"
      );
      if (dryingPackupLifecycleState) {
        return dryingPackupLifecycleState;
      }
    }
    if (
      dryingOverview.packupDevicesFinishedBy &&
      Array.isArray(dryingConclusionResults) &&
      dryingConclusionResults.length >= 0
    ) {
      const latestDryingConclusionResult = dryingConclusionResults[dryingConclusionResults.length - 1];
      if (latestDryingConclusionResult) {
        return getDryingResultInfo(latestDryingConclusionResult, dryingOverview);
      }
    }

    const dryingSetupCompletions = projectSteps?.dryingSetupCompletions;
    if (Array.isArray(dryingSetupCompletions) && dryingSetupCompletions.length > 0) {
      const latestDryingSetupCompletion = dryingSetupCompletions[dryingSetupCompletions.length - 1];
      const dryingSetupLifecycleState = getLifecycleProjectState(
        latestDryingSetupCompletion,
        "dryingSetupSuspended",
        "dryingSetupResumed"
      );
      if (dryingSetupLifecycleState) {
        return dryingSetupLifecycleState;
      }
    }

    const intermediateMeasurementCompletions = projectSteps?.intermediateMeasurementCompletions;
    if (Array.isArray(intermediateMeasurementCompletions) && intermediateMeasurementCompletions.length > 0) {
      const latestIntermediateMeasurement = intermediateMeasurementCompletions[intermediateMeasurementCompletions.length - 1];
      const intermediateMeasurementLifecycleState = getLifecycleProjectState(
        latestIntermediateMeasurement,
        "intermediateMeasurementSuspended",
        "intermediateMeasurementResumed"
      );
      if (intermediateMeasurementLifecycleState) {
        return intermediateMeasurementLifecycleState;
      }
    }

    if (
      dryingOverview.dryingSetupComplete == true ||
      projectSteps?.dryingSetupCompletions
    ) {
      return getDryingStartedInfo(
        ProjectStateType.DryingStarted,
        estimatedDryingDurationInDays,
        dryingOverview
      );
    }

    if (dryingOverview.setupDryingStartedBy) {
      return getDryingStartedInfo(
        ProjectStateType.DryingPreperationRunning,
        estimatedDryingDurationInDays,
        dryingOverview
      );
    }

    return getDryingStartedInfo(
      ProjectStateType.DryingStarted,
      estimatedDryingDurationInDays,
      dryingOverview
    );
  }

  if (
    projectOverview.allAssignmentsClosed === undefined ||
    projectOverview.allAssignmentsClosed === null
  ) {
    const nonGzLikeState = getLatestDryingProjectState(dryingOverview, today);
    if (
      nonGzLikeState &&
      nonGzLikeState.projectStepType !== ProjectStateType.DryingApproved
    ) {
      return nonGzLikeState;
    }

    const hasWebsiteDryingSignals =
      !!dryingOverview.setupDryingStartedBy ||
      dryingOverview.dryingSetupComplete == true ||
      !!dryingOverview.packupDevicesFinishedBy ||
      !!projectSteps?.dryingSetupAppointments?.length ||
      !!projectSteps?.dryingSetupCompletions?.length ||
      !!projectSteps?.intermediateMeasurementAppointments?.length ||
      !!projectSteps?.intermediateMeasurementCompletions?.length ||
      !!projectSteps?.dryingConclusionAppointments?.length ||
      !!projectSteps?.dryingConclusionResults?.length;

    if (hasWebsiteDryingSignals) {
      // Keep portal-only fallback aligned with historical GZ behavior when step fields exist
      // but allAssignmentsClosed was not persisted by upstream systems.
      const estimatedDryingDurationInDays =
        dryingOverview.estimatedDryingDurationInDays ??
        projectSteps?.offerResult?.estimatedDryingTime ??
        projectSteps?.damageAssessmentResult?.estimatedDryingTime;

      const nextAppointment = getNextDryingOrRestorationAppointment(dryingOverview, today, true);
      if (nextAppointment) {
        return nextAppointment;
      }

      const dryingConclusionResults = projectSteps?.dryingConclusionResults;
      if (Array.isArray(dryingConclusionResults) && dryingConclusionResults.length > 0) {
        const latestDryingConclusionResult = dryingConclusionResults[dryingConclusionResults.length - 1];
        const dryingPackupLifecycleState = getLifecycleProjectState(
          latestDryingConclusionResult,
          "dryingPackupSuspended",
          "dryingPackupResumed"
        );
        if (dryingPackupLifecycleState) {
          return dryingPackupLifecycleState;
        }
      }
      if (
        dryingOverview.packupDevicesFinishedBy &&
        Array.isArray(dryingConclusionResults) &&
        dryingConclusionResults.length >= 0
      ) {
        const latestDryingConclusionResult = dryingConclusionResults[dryingConclusionResults.length - 1];
        if (latestDryingConclusionResult) {
          return getDryingResultInfo(latestDryingConclusionResult, dryingOverview);
        }
      }

      const dryingSetupCompletions = projectSteps?.dryingSetupCompletions;
      if (Array.isArray(dryingSetupCompletions) && dryingSetupCompletions.length > 0) {
        const latestDryingSetupCompletion = dryingSetupCompletions[dryingSetupCompletions.length - 1];
        const dryingSetupLifecycleState = getLifecycleProjectState(
          latestDryingSetupCompletion,
          "dryingSetupSuspended",
          "dryingSetupResumed"
        );
        if (dryingSetupLifecycleState) {
          return dryingSetupLifecycleState;
        }
      }

      const intermediateMeasurementCompletions = projectSteps?.intermediateMeasurementCompletions;
      if (Array.isArray(intermediateMeasurementCompletions) && intermediateMeasurementCompletions.length > 0) {
        const latestIntermediateMeasurement = intermediateMeasurementCompletions[intermediateMeasurementCompletions.length - 1];
        const intermediateMeasurementLifecycleState = getLifecycleProjectState(
          latestIntermediateMeasurement,
          "intermediateMeasurementSuspended",
          "intermediateMeasurementResumed"
        );
        if (intermediateMeasurementLifecycleState) {
          return intermediateMeasurementLifecycleState;
        }
      }

      if (
        dryingOverview.dryingSetupComplete == true ||
        projectSteps?.dryingSetupCompletions
      ) {
        return getDryingStartedInfo(
          ProjectStateType.DryingStarted,
          estimatedDryingDurationInDays,
          dryingOverview
        );
      }

      if (dryingOverview.setupDryingStartedBy) {
        return getDryingStartedInfo(
          ProjectStateType.DryingPreperationRunning,
          estimatedDryingDurationInDays,
          dryingOverview
        );
      }

      return getDryingStartedInfo(
        ProjectStateType.DryingStarted,
        estimatedDryingDurationInDays,
        dryingOverview
      );
    }
  }

  if (steps.projectApproved) {
    // Check for pending drying/conclusion appointments before falling back to AssignDevices
    const nextAppointment = getNextDryingOrRestorationAppointment(
      projectOverview as DryingProjectOverview,
      today,
      true
    );

    if (nextAppointment) {
      return nextAppointment;
    }

    // Portal-only projects automatically receive step 9 (Freigabe Trocknung)
    // when the project is approved, so projectStatus alone determines readiness
    // for device assignment — no supervisor/owner guard needed.
    return {
      projectStepType: ProjectStateType.AssignDevices,
    };
  }

  return {
    projectStepType: ProjectStateType.ApproveProject,
  };
}

export function getLastestProjectState(
  projectOverview: ProjectOverview,
  today: Date
): ProjectStateInfo | null {
  const isLeakDetectionProject = getIsLeakDetectionProject(projectOverview.id);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let steps: any | undefined = undefined;

  if (isLeakDetectionProject) {
    const leakDetectionProjectOverview = projectOverview as LeakDetectionProjectOverview;
    steps = leakDetectionProjectOverview.leakDetectionProjectSteps;
  } else {
    const dryingProjectOverview = projectOverview as DryingProjectOverview;
    steps = dryingProjectOverview.dryingProjectSteps;
  }

  if (!steps) {
    return null;
  }

  if (isProjectOverviewWebsiteOnly(projectOverview)) {
    return getLatestProjectStateOfWebsiteOnlyProject(projectOverview, steps, today);
  }

  if (steps.projectApproved && steps.projectApproved.orderAccepted === 0) {
    //Project was rejected
    return getProjectApprovalInfo(steps.projectApproved, projectOverview);
  }

  if (!steps.damageAssessmentResult) {
    const nextAppointments = [
      getNextAppointment(
        steps?.damageAssessmentAppointment
          ? [steps.damageAssessmentAppointment]
          : [],
        today,
        AppointmentType.DAMAGE_ASSESSMENT
      ),
      getNextAppointment(
        steps?.leakDetectionAppointment ? [steps.leakDetectionAppointment] : [],
        today,
        AppointmentType.LEAK_DETECTION
      ),
    ];

    const nextAppointment =
      nextAppointments
        .filter((appointment) => !!appointment)
        .sort(
          (a, b) =>
            (a?.appointmentDate?.getTime() ?? Number.MAX_SAFE_INTEGER) -
            (b?.appointmentDate?.getTime() ?? Number.MAX_SAFE_INTEGER)
        )[0] ?? null;

    if (nextAppointment) {
      return nextAppointment;
    }
  }

  if (!isLeakDetectionProject) {
    const dryingProjectOverview = projectOverview as DryingProjectOverview;

    if (hasValue(dryingProjectOverview.damagePatternConclusionComplete)) {
      const latestDryingStep = getLatestDryingProjectState(
        dryingProjectOverview,
        today
      );

      if (latestDryingStep) {
        return latestDryingStep;
      }
    }
  }

  const leakDetectionResult = steps.leakDetectionResult;

  const doesProjectContainDryingInfo = containsDryingInfo(projectOverview);
  const doesProjectContainLeakDetectionInfo = containsLeakDetectionInfo(
    projectOverview
  );

  if (leakDetectionResult) {
    const leakDetectionLifecycleState = getLifecycleProjectState(
      leakDetectionResult,
      "leakDetectionSuspended",
      "leakDetectionResumed"
    );
    if (leakDetectionLifecycleState) {
      return leakDetectionLifecycleState;
    }
    if (projectOverview.isFinished) {
      return getProjectFinishedInfo(
        leakDetectionResult,
        ProjectPhase.LeakDetection
      );
    }/*  else if (doesProjectContainDryingInfo) {
      return {
        projectStepType: ProjectStateType.DamageAssessmentRunning,
      };
    }  */else {
      return getLeakDetectionDoneInfo(leakDetectionResult);
    }
  }

  if (isLeakDetectionProject) {
    const leakDetectionProjectOverview = projectOverview as LeakDetectionProjectOverview;

    if (leakDetectionProjectOverview.leakDetectionPreparationStartedBy) {
      return {
        projectStepType: ProjectStateType.LeakDetectionRunning,
      };
    }
  } else {
    const dryingProjectOverview = projectOverview as DryingProjectOverview;

    if (
      hasValue(dryingProjectOverview.documentationAndLeakDetectionComplete) ||
      hasValue(dryingProjectOverview.damagePatternComplete) ||
      hasValue(dryingProjectOverview.damagePatternConclusionComplete)
    ) {
      if (!doesProjectContainLeakDetectionInfo) {
        return {
          projectStepType: ProjectStateType.DamageAssessmentRunning,
        };
      } else if (!doesProjectContainDryingInfo) {
        return {
          projectStepType: ProjectStateType.LeakDetectionRunning,
        };
      } else {
        return {
          projectStepType: ProjectStateType.LeakDetectionRunning,
        };
      }
    }
  }

  if (steps.firstContact) {
    return {
      projectStepType: ProjectStateType.FirstContactDone,
    };
  }

  if (steps.projectApproved) {
    return getProjectApprovalInfo(steps.projectApproved, projectOverview);
  }

  return {
    projectStepType: ProjectStateType.ApproveProject,
  };
}

function areAllAppointmentsCompleted(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  appointment: any,
) {
  let isCompleted = true;

  if (!appointment) {
    return false;
  }

  const appointmentArray = Array.isArray(appointment) ? appointment : [appointment];

  for (const item of appointmentArray) {
    if (!item.completed) {
      isCompleted = false;
      
      break;
    }
  }

  return isCompleted;
}

export function getLastestProjectStateForProjectTypeDisplay(
  projectOverview: ProjectOverview,
  today: Date
): ExtendedDefaultProjectStepInfo | null {
  const hasDrying = containsDryingInfo(projectOverview);
  const isLeakDetectionProject = getIsLeakDetectionProject(projectOverview.id);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let steps: any | undefined = undefined;

  if (isLeakDetectionProject) {
    const leakDetectionProjectOverview = projectOverview as LeakDetectionProjectOverview;
    steps = leakDetectionProjectOverview.leakDetectionProjectSteps;
  } else {
    const dryingProjectOverview = projectOverview as DryingProjectOverview;
    steps = dryingProjectOverview.dryingProjectSteps;
  }

  if (!steps) {
    return null;
  }

  const wasDryingStarted =
    !isNullOrEmpty(steps.dryingSetupAppointments) ||
    !isNullOrEmpty(steps.dryingSetupCompletions);

  if (wasDryingStarted) {
    const nextDryingAppointment = getNextDryingOrRestorationAppointment(
      projectOverview as DryingProjectOverview,
      today,
      hasDrying
    );
    if (nextDryingAppointment != null) return nextDryingAppointment;

    if (steps.intermediateMeasurementAppointments !== null) {
      if (
        steps.intermediateMeasurementAppointments?.size !==
        (steps.intermediateMeasurementCompletions?.size ?? 0)
      ) {
        return {
          projectStepType: ProjecStateForProjectTypeDisplay.IntermediateMeasurementRunning
        };
      }
      else if (
        steps.dryingConclusionAppointments === null
      ) {
        return {
          projectStepType: ProjecStateForProjectTypeDisplay.IntermediateMeasurementDone
        };
      }
    }

    const dryingConclusionResults = steps.dryingConclusionResults;
    if (
      !isNullOrEmpty(steps.dryingConclusionAppointments) &&
      isNullOrEmpty(dryingConclusionResults) &&
      areAllAppointmentsCompleted(steps.dryingSetupAppointments) === true
    ) {
      return {
        projectStepType: ProjecStateForProjectTypeDisplay.DryingConclusionRunning
      };
    }
  }

  const latestProjectState = getLastestProjectState(projectOverview, today);

  if (!latestProjectState) {
    return null;
  }
  if (latestProjectState.projectStepType === ProjectStateType.Appointment) {
    const appointmentInfo = latestProjectState as AppointmentStepInfo;

    return {
      projectStepType: latestProjectState.projectStepType,
      appointmentType: appointmentInfo.appointmentType
    }
  }

  return { projectStepType: latestProjectState.projectStepType };
}

/**
 * Maps the latest project state (as returned by
 * {@link getLastestProjectStateForProjectTypeDisplay}) to the background colour
 * of the project-type badge shown in the project overview list.
 *
 * Returns an empty string when there is no resolvable state, which the badge
 * renders as no background (transparent).
 */
export function getProjectTypeDisplayColor(
  latestProjectState: ExtendedDefaultProjectStepInfo | null
): string {
  const projectStateType = latestProjectState?.projectStepType;
  const appointmentType = latestProjectState?.appointmentType;

  if (!projectStateType) {
    return "";
  }

  switch (projectStateType) {
    case ProjectStateType.ApproveProject:
    case ProjectStateType.ProjectApprovalDone:
    case ProjectStateType.SetProjectSupervisor:
    case ProjectStateType.FirstContactDone:
      return COLOR_PROJECT_TYPE_STATE_01;
    case ProjectStateType.LeakDetectionRunning:
    case ProjectStateType.LeakDetectionFinished:
      return COLOR_PROJECT_TYPE_STATE_02;
    case ProjectStateType.DamageAssessmentRunning:
    case ProjectStateType.DamageAssessmentDone:
      return COLOR_PROJECT_TYPE_STATE_03;
    case ProjectStateType.DryingApproved:
    case ProjectStateType.InsuranceOfferSend:
    case ProjectStateType.InsuranceOfferResult:
      return COLOR_PROJECT_TYPE_STATE_04;
    case ProjectStateType.AssignDevices:
      return COLOR_PROJECT_TYPE_STATE_05;
    // PhaseSuspended/PhaseResumed are treated as "drying running" everywhere else
    // (getProjectStateNumber maps both to 16 = DryingStarted), so colour them the same.
    case ProjectStateType.DryingPreperationRunning:
    case ProjectStateType.DryingStarted:
    case ProjectStateType.PhaseSuspended:
    case ProjectStateType.PhaseResumed:
      return COLOR_PROJECT_TYPE_STATE_06;
    case ProjecStateForProjectTypeDisplay.DryingConclusionRunning:
    case ProjectStateType.DryingResult:
    case ProjecStateForProjectTypeDisplay.IntermediateMeasurementRunning:
    case ProjecStateForProjectTypeDisplay.IntermediateMeasurementDone:
      return COLOR_PROJECT_TYPE_STATE_07;
    case ProjectStateType.RepairStarted:
    case ProjectStateType.RepairCompleted:
      return COLOR_PROJECT_TYPE_STATE_08;
    case ProjectStateType.ProjectFinished:
    case ProjectStateType.ConcludeProject:
      return COLOR_PROJECT_TYPE_STATE_09;
    case ProjectStateType.Appointment: {
      switch (appointmentType) {
        case AppointmentType.LEAK_DETECTION:
          return COLOR_APPOINTMENT_LEAK_DETECTION;
        case AppointmentType.DAMAGE_ASSESSMENT:
          return COLOR_APPOINTMENT_DAMAGE_ASSESSMENT;
        case AppointmentType.PREPARE_DRYING:
          return COLOR_APPOINTMENT_DRYING_PREPARATION;
        case AppointmentType.INTERMEDIATE_MEASUREMENT:
          return COLOR_APPOINTMENT_INTERMEDIATE_MEASUREMENT;
        case AppointmentType.DRYING_CONCLUSION:
          return COLOR_APPOINTMENT_DRYING_CONCLUSION;
        case AppointmentType.REPAIR:
          return COLOR_APPOINTMENT_REPAIR;
        case AppointmentType.FOLLOW_UP_INSPECTION:
          return COLOR_APPOINTMENT_FOLLOW_UP;

        default:
          return COLOR_APPOINTMENT_DEFAULT;
      }
    }

    default:
      return "";
  }
}

export function getProjectStateNumber(
  projectStateInfo: ProjectStateInfo
): number {
  const projectStateType = projectStateInfo.projectStepType;

  switch (projectStateType) {
    case ProjectStateType.SetProjectSupervisor:
      return 2;
    case ProjectStateType.AssignDevices:
      return 11;
    case ProjectStateType.ApproveProject:
      return 1;
    case ProjectStateType.ProjectApprovalDone:
      return 2;
    case ProjectStateType.FirstContactDone:
      return 3;
    case ProjectStateType.LeakDetectionRunning:
      return 5;
    case ProjectStateType.LeakDetectionFinished:
      return 6;
    case ProjectStateType.Appointment: {
      const appointmentInfo = projectStateInfo as AppointmentStepInfo;

      switch (appointmentInfo.appointmentType) {
        case AppointmentType.DAMAGE_ASSESSMENT:
          return 7;
        case AppointmentType.LEAK_DETECTION:
          return 4;
        case AppointmentType.PREPARE_DRYING:
          return 14;
        case AppointmentType.INTERMEDIATE_MEASUREMENT:
          return 18;
        case AppointmentType.DRYING_CONCLUSION:
          return 19;
        case AppointmentType.REPAIR:
          return 21;
        case AppointmentType.FOLLOW_UP_INSPECTION:
          return 17;
      }
      break;
    }
    case ProjectStateType.DamageAssessmentRunning:
      return 8;
    case ProjectStateType.DamageAssessmentDone: {
      const info = projectStateInfo as DamageAssessmentDoneInfo;

      if (info.noDryingNecessary) {
        return 25;
      }

      return 9;
    }
    case ProjectStateType.InsuranceOfferSend:
      return 11;
    case ProjectStateType.InsuranceOfferResult:
      return 12;
    case ProjectStateType.DryingApproved:
      return 13;
    case ProjectStateType.DryingPreperationRunning:
      return 15;
    case ProjectStateType.DryingStarted:
      return 16;
    case ProjectStateType.DryingResult:
      return 20;
    case ProjectStateType.RepairStarted:
      return 22;
    case ProjectStateType.RepairCompleted:
      return 23;
    case ProjectStateType.PhaseSuspended:
      return 16;
    case ProjectStateType.PhaseResumed:
      return 16;
    case ProjectStateType.ConcludeProject:
      return 24;
    case ProjectStateType.ProjectFinished:
      return 25;
  }
}

/**
 * Determines whether device assignment is allowed based on the current project state.
 *
 * The allowed state ranges (using the 18-step project workflow) are:
 *
 * **Portal-only projects:**
 *   - Allowed from step 9 "Freigabe Trocknung" (state number >= 11, i.e. AssignDevices)
 *     up to but not including step 18 "Projekt abgeschlossen" (state number < 25, i.e. ProjectFinished).
 *
 * **Non-portal projects:**
 *   - Allowed from step 6 "Beginn Schadenaufnahme" (state number >= 7, i.e. Appointment:DAMAGE_ASSESSMENT)
 *     up to but not including step 15 "Abschluss Trocknung" (state number < 20, i.e. DryingResult).
 *
 * Phase reopening, suspension, and resuming are handled implicitly:
 *   - **Reopening**: When a phase is reopened, the underlying project data is modified
 *     (e.g. conclusion data removed), causing `getLastestProjectState()` to return an earlier state.
 *     This naturally adjusts the state number and may re-enable device assignment.
 *   - **Suspension**: A suspended phase still retains its step data, so `getLastestProjectState()`
 *     continues to return the same state. Device assignment remains allowed if state is in range.
 *   - **Resuming**: Resuming a suspended phase does not change the state number; the project
 *     continues from where it was paused.
 *
 * @param projectOverview - The project overview data.
 * @param today - The current date (used to determine next appointments, etc.).
 * @returns `true` if device assignment is allowed, `false` otherwise.
 */
export function isDeviceAssignmentAllowedByProjectState(
  projectOverview: ProjectOverview,
  today: Date,
): boolean {
  const latestState = getLastestProjectState(projectOverview, today);
  if (!latestState) return false;

  const stateNumber = getProjectStateNumber(latestState);
  const isPortalOnly = isProjectOverviewWebsiteOnly(projectOverview);

  if (isPortalOnly) {
    // Portal-only: step 9 "Freigabe Trocknung" → AssignDevices (state number 11)
    //              step 18 "Projekt abgeschlossen" → ProjectFinished (state number 25)
    const MIN_STATE_PORTAL = 11;
    const MAX_STATE_PORTAL = 25;
    return stateNumber >= MIN_STATE_PORTAL && stateNumber < MAX_STATE_PORTAL;
  } else {
    // Non-portal: step 6 "Beginn Schadenaufnahme" → Appointment:DAMAGE_ASSESSMENT (state number 7)
    //             step 15 "Abschluss Trocknung" → DryingResult (state number 20)
    const MIN_STATE_DEFAULT = 7;
    const MAX_STATE_DEFAULT = 20;
    return stateNumber >= MIN_STATE_DEFAULT && stateNumber < MAX_STATE_DEFAULT;
  }
}

export function getProjectOverviewUrgencyValue(
  projectOverview: ProjectOverview,
  projectStateInfo: ProjectStateInfo | null
): number {
  const fallbackTimestamp = Number.MAX_VALUE;

  if (
    projectStateInfo &&
    projectStateInfo.projectStepType === ProjectStateType.Appointment
  ) {
    const appointmentStepInfo = projectStateInfo as AppointmentStepInfo;

    if (
      isAppointmentStepInfoComplete(appointmentStepInfo) ||
      (appointmentStepInfo.appointmentType ===
        AppointmentType.FOLLOW_UP_INSPECTION &&
        appointmentStepInfo.appointmentDate)
    ) {
      return (
        appointmentStepInfo.appointmentDate?.getTime() ?? fallbackTimestamp
      );
    }
  }

  const projectStepContainer = getProjectStepContainer(projectOverview);

  if (!projectStepContainer) {
    return fallbackTimestamp;
  }

  let latestStepTimestamp: number | undefined | null = undefined;

  if (getIsLeakDetectionProject(projectOverview.id)) {
    const leakDetectionProjectOverview = projectOverview as LeakDetectionProjectOverview;

    latestStepTimestamp =
      getStepCreationTimestamp(projectStepContainer.leakDetectionResult) ??
      leakDetectionProjectOverview.leakDetectionPreparationStartedBy?.recordedAt?.toMillis();
  } else {
    const dryingProjectOverview = projectOverview as DryingProjectOverview;
    const dryingSetupCompletions = projectStepContainer.dryingSetupCompletions;

    if (isProjectOverviewWebsiteOnly(dryingProjectOverview)) {
      latestStepTimestamp =
        projectOverview.endDate?.toMillis() ??
        getStepCreationTimestamp(projectStepContainer.projectResult) ??
        getStepCreationTimestamp(
          projectStepContainer.dryingConclusionResults
        ) ??
        getStepCreationTimestamp(projectStepContainer.dryingSetupCompletions) ??
        getStepCreationTimestamp(projectStepContainer.offerResult);
    } else {
      latestStepTimestamp =
        getStepCreationTimestamp(projectStepContainer.projectResult) ??
        dryingProjectOverview.repairCompletedBy?.recordedAt?.toMillis() ??
        getStepCreationTimestamp(projectStepContainer.repairStart) ??
        dryingProjectOverview.packupDevicesFinishedBy?.recordedAt?.toMillis() ??
        getStepCreationTimestamp(
          Array.isArray(dryingSetupCompletions) &&
            dryingSetupCompletions.length > 0
            ? dryingSetupCompletions[dryingSetupCompletions.length - 1]
            : null
        ) ??
        getStepCreationTimestamp(projectStepContainer.damageAssessmentResult);
    }
  }

  return (
    latestStepTimestamp ??
    getStepCreationTimestamp(projectStepContainer.firstContact) ??
    getStepCreationTimestamp(projectStepContainer.projectAccepted) ??
    getStepCreationTimestamp(projectStepContainer.projectApproved) ??
    fallbackTimestamp
  );
}

function labelAndStatus(
  label?: string | null,
  status?: string | null,
) {
  if (!label && !status) {
    return "";
  }
  else if (!status) {
    return label;
  }
  else if (!label) {
    return status;
  }
  else {
    return `${label} / ${status}`;
  }
}

export function labelAndStatusAppointments(
  vue: Vue,
  appointmentDate: Date | null,
  appointmentType: AppointmentType,
  apartmentNumber: number | null,
  responsibleUser: string | null,
  hasMultipleApartments = false,
) {
  const userLookup = new UserLookup(UserModule.activeUsersOfClientIncludingIresAdmin);

  if (appointmentType === AppointmentType.FOLLOW_UP_INSPECTION) {
    const date = appointmentDate;
    const status = vue.$t("projectLogs.eventLogs.followUpInspectionDate", [formatDateShort(date ?? new Date())]).toString();
    const label = vue.$t("projectLogs.eventLogs.dryingRunning").toString();

    return labelAndStatus(label, status);
  }

  const user = responsibleUser ? userLookup.getUserByIdOrNicknameOrStaffNumber(responsibleUser) : null;
  const nickname = user?.nickname ?? null;

  const date = appointmentDate && appointmentDate.getTime() !== Number.MAX_SAFE_INTEGER ? appointmentDate : null;
  const dateString = date ? formatJsDate(date, true) : vue.$t("projectStep.appointmentMissingDate").toString();

  const label = getAppointmentDescriptionForProjectEventLog(
    vue,
    appointmentType,
  );

  const apartmentInfo = hasMultipleApartments && apartmentNumber !== null && apartmentNumber !== 0
    ? `(${vue.$t("clientData.apartmentNumber", [apartmentNumber])})`
    : null;

  const statusText = joinToString(
    [
      nickname ?? vue.$t("projectStep.appointmentMissingResponsibleUser").toString(),
      dateString,
      apartmentInfo
    ],
    " "
  );

  return labelAndStatus(
    label,
    statusText
  )
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getProjectStepCreationTimestamp(projectStep: any) { 
  if (!projectStep) {
    return "";
  }

  const isStepResumed = getIsStepResumed(projectStep, projectStep?.phaseReOpened);
  const isStepSuspended = getIsStepSuspended(projectStep, projectStep?.phaseReOpened);

  let creationTimestamp = projectStep.creationTimestamp;

  if (isStepResumed) {
    creationTimestamp = projectStep.phaseSuspended.resumedBy.creationTimestamp;
  }
  else if (isStepSuspended) {
    creationTimestamp = projectStep.phaseSuspended.suspendedBy.creationTimestamp;
  }

  return creationTimestamp;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getProjectStepCreatedBy(projectStep: any) { 
  if (!projectStep) {
    return "";
  }

  const isStepResumed = getIsStepResumed(projectStep, projectStep?.phaseReOpened);
  const isStepSuspended = getIsStepSuspended(projectStep, projectStep?.phaseReOpened);

  let createdBy = projectStep.createdBy;

  if (isStepResumed) {
    createdBy = projectStep.phaseSuspended.resumedBy.createdBy;
  }
  else if (isStepSuspended) {
    createdBy = projectStep.phaseSuspended.suspendedBy.createdBy;
  }

  return createdBy;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getResourceKey(projectStep: any) {
  const isStepResumed = getIsStepResumed(projectStep, projectStep?.phaseReOpened);
  const isStepSuspended = getIsStepSuspended(projectStep, projectStep?.phaseReOpened);

  let resourceKey = "Finished";

  if (isStepSuspended) {
    resourceKey = "Suspended";
  }
  else if (isStepResumed) {
    resourceKey = "Resumed";
  }

  return resourceKey;
}

function formatEstimatedDryingTime(
  vue: Vue,
  estimatedDryingTimeInDays?: number,
) {
  if (estimatedDryingTimeInDays === null) {
    return null;
  }

  return vue.$tc("projectLogs.projectState.dryingDuration", estimatedDryingTimeInDays);
}

function getProjectAcceptedStepDescription(
  vue: Vue,
  hideQuestion?: boolean,
  showOnlyLabel?: boolean,
) {
  const label = vue.$t("projectLogs.projectState.orderRecorded").toString();
  const status = !hideQuestion
    ? vue.$t("projectLogs.projectState.approveProject").toString()
    : null;

  return labelAndStatus(label, !showOnlyLabel ? status : null);
}

function getProjectApprovedDescription(
  vue: Vue,
  orderAccepted: number,
  hideQuestion?: boolean,
  showOnlyLabel?: boolean,
) {
  if (orderAccepted === 0) {
    return labelAndStatus(
      vue.$t("projectLogs.projectState.projectItemFinished").toString(),
      !showOnlyLabel
        ? vue.$t("projectLogs.projectState.orderRejected").toString()
        : null
    );
  }

  const label = vue.$t("projectLogs.projectState.projectApproved").toString();
  const status = !hideQuestion
    ? vue.$t("projectLogs.projectState.establishFirstContact").toString()
    : null;

  return labelAndStatus(label, !showOnlyLabel ? status : null);
}

function getFirstContactStepDescription(
  vue: Vue,
  hideQuestion?: boolean,
  showOnlyLabel?: boolean,
) {
  const label = vue.$t("projectLogs.projectState.firstContactEstablished").toString();
  const status = !hideQuestion
    ? vue.$t("projectLogs.projectState.makeAppointment").toString()
    : null;

  return labelAndStatus(label, !showOnlyLabel ? status : null);
}

function getDryingPreparationResult(
  vue: Vue,
  contactPersonNumber: number,
  hasMultipleApartments: boolean,
  estimatedDryingTimeInDays?: number,
  showOnlyLabel?: boolean,
  resourceKey = "Finished"
) {
  const label = resourceKey !== "Finished"
    ? vue.$t(`projectLogs.projectState.dryingSetup${resourceKey}`).toString()
    : vue.$t("projectLogs.projectState.dryingRunning").toString();
  const status = formatEstimatedDryingTime(vue, estimatedDryingTimeInDays);

  return labelAndStatus(
    joinToString([
      label,
      hasMultipleApartments 
      ? contactPersonNumber !== 0
        ? vue.$t("projectLogs.projectState.apartmentNumber", [contactPersonNumber]).toString()
        : vue.$t("projectLogs.projectState.allApartments").toString()
      : null,
    ], ""),
    !showOnlyLabel ? status : null
  );
}

function getRepairResult(
  vue: Vue,
  contactPersonNumber: number,
  hasMultipleApartments: boolean,
  resourceKey = "Finished"
) {
  return labelAndStatus(
    joinToString([
      vue.$t(`projectLogs.projectState.repair${resourceKey}`).toString(),
      hasMultipleApartments 
      ? contactPersonNumber !== 0
        ? vue.$t("projectLogs.projectState.apartmentNumber", [contactPersonNumber]).toString()
        : vue.$t("projectLogs.projectState.allApartments").toString()
      : null,
    ], "")
  );
}

function getOfferSendToInsuranceDescription(vue: Vue) {
  return labelAndStatus(
    vue.$t("projectLogs.projectState.offerSendToInsurance").toString()
  );
}

function getRepairConcludedDescription(vue: Vue) {
  return labelAndStatus(
    vue.$t("projectLogs.projectState.repairFinished").toString()
  );
}

function getLeakDetectionPhaseBegunDescription(vue: Vue) {
  return labelAndStatus(
    vue.$t("projectLogs.projectState.leakDetectionBegun").toString()
  );
}

function getDamageAssessmentPhaseBegunDescription(vue: Vue) {
  return labelAndStatus(
    vue.$t("projectLogs.projectState.damageAssessmentBegun").toString()
  );
}

function getDryingSetupPhaseBegunDescription(
  vue: Vue,
  contactPersonNumber: number,
  hasMultipleApartments: boolean
) {
  return labelAndStatus(
    joinToString([
      vue.$t("projectLogs.projectState.dryingSetupBegun").toString(),
      hasMultipleApartments 
      ? contactPersonNumber !== 0
        ? vue.$t("projectLogs.projectState.apartmentNumber", [contactPersonNumber]).toString()
        : vue.$t("projectLogs.projectState.allApartments").toString()
      : null,
    ], "")
  );
}

function getIntermediateMeasurementPhaseBegunDescription(
  vue: Vue,
  contactPersonNumber: number,
  hasMultipleApartments: boolean
) {
  return labelAndStatus(
    joinToString([
      vue.$t("projectLogs.projectState.intermediateMeasurementBegun").toString(),
      hasMultipleApartments 
      ? contactPersonNumber !== 0
        ? vue.$t("projectLogs.projectState.apartmentNumber", [contactPersonNumber]).toString()
        : vue.$t("projectLogs.projectState.allApartments").toString()
      : null,
    ], "")
  );
}

function getIntermediateMeasurementResultDescription(
  vue: Vue,
  contactPersonNumber: number,
  hasMultipleApartments: boolean,
  resourceKey = "Finished"
) {
  return labelAndStatus(
    joinToString([
      vue.$t(`projectLogs.projectState.intermediateMeasurement${resourceKey}`).toString(),
      hasMultipleApartments 
      ? contactPersonNumber !== 0
        ? vue.$t("projectLogs.projectState.apartmentNumber", [contactPersonNumber]).toString()
        : vue.$t("projectLogs.projectState.allApartments").toString()
      : null,
    ], "")
  );
}

function getDryingPackupBegunDescription(
  vue: Vue,
  contactPersonNumber: number,
  hasMultipleApartments: boolean
) {
  return labelAndStatus(
    joinToString([
      vue.$t("projectLogs.projectState.dryingPackupBegun").toString(),
      hasMultipleApartments 
      ? contactPersonNumber !== 0
        ? vue.$t("projectLogs.projectState.apartmentNumber", [contactPersonNumber]).toString()
        : vue.$t("projectLogs.projectState.allApartments").toString()
      : null,
    ], "")
  );
}

function getRepairBegunDescription(
  vue: Vue,
  contactPersonNumber: number,
  hasMultipleApartments: boolean
) {
  return labelAndStatus(
    joinToString([
      vue.$t("projectLogs.projectState.repairBegun").toString(),
      hasMultipleApartments 
      ? contactPersonNumber !== 0
        ? vue.$t("projectLogs.projectState.apartmentNumber", [contactPersonNumber]).toString()
        : vue.$t("projectLogs.projectState.allApartments").toString()
      : null,
    ], "")
  );
}

function formatReportStep(
  vue: Vue,
  label: string,
  projectReportInfo: ProjectReportInfo,
  showOnlyLabel?: boolean,
) {
  const status = projectReportInfo.downloadRefExternalPdfReport === null
    ? vue.$t("projectLogs.projectState.noReport").toString()
    : vue.$t("projectLogs.projectState.reportCreated").toString();

  return labelAndStatus(label, !showOnlyLabel ? status : null);
}

function formatLeakDetectionResultStep(
  vue: Vue,
  resultInfo: ProjectReportInfo,
  showOnlyLabel?: boolean,
  resourceKey = "Finished"
) {
  return formatReportStep(
    vue,
    vue.$t(`projectLogs.projectState.leakDetection${resourceKey}`).toString(),
    resultInfo,
    showOnlyLabel
  );
}

function getProjectResultDescription(
  vue: Vue,
  projectResultStep: ProjectResultStep,
  showOnlyLabel?: boolean,
) {
  return formatReportStep(
    vue,
    vue.$t("projectLogs.projectState.projectItemFinished").toString(),
    projectResultStep,
    showOnlyLabel
  );
}

function formatDamageAssessmentResultStepForDryingStep(
  vue: Vue,
  damageAssessmentResultStep: DamageAssessmentResultStep,
  project: ProjectDetails,
  showOnlyLabel?: boolean,
  hasDrying = true,
  resourceKey = "Finished"
) {
  const currentConclusionState = project.finishDamagePatternData?.conclusionState ?? null;

  const dryingProgressDescription = currentConclusionState 
    ? nullIfBlank(
        getDamagePatternConclusionStateString(
          vue,
          currentConclusionState,
          hasDrying
        )
      )
    : null;
  
  const label = resourceKey !== "Finished" 
    ? vue.$t(`projectLogs.projectState.damageAssessment${resourceKey}`).toString()
    : joinToString(
      [
        vue.$t("projectLogs.projectState.damageAssessmentCompletedWithoutTile").toString(),
        dryingProgressDescription
      ],
      ", "
    );

  let status = null;

  if (damageAssessmentResultStep.downloadRefInternalPdfReport === null) {
    status = vue.$t("projectLogs.projectState.noReport").toString();
  }
  else if (damageAssessmentResultStep.approvalStatus === 1) {
    status = vue.$t("projectLogs.projectState.projectOnHold").toString();
  }
  else if (damageAssessmentResultStep.approvalStatus === 0) {
    status = vue.$t(`projectLogs.projectState.${hasDrying ? "noDrying" : "noRestoration"}`).toString();
  }

  return labelAndStatus(label, !showOnlyLabel ? status : null);
}

function getDryingConclusionResultDescription(
  vue: Vue,
  dryingConclusionResult: DryingConclusionResult,
  dryingProjectOverview: DryingProjectOverview,
  contactPersonNumber: number,
  hasMultipleApartments: boolean,
  showOnlyLabel?: boolean,
  resourceKey = "Finished"
) {
  let status = null;

  if (dryingProjectOverview?.repairAction === RepairAction.IMMEDIATELY) {
    status = vue.$t("projectLogs.projectState.repairStarted").toString();
  }
  else if (dryingProjectOverview?.repairAction === RepairAction.APPOINTMENT) {
    status = vue.$t("projectLogs.projectState.makeAppointment").toString();
  }
  else if (
    dryingProjectOverview?.repairAction === RepairAction.NOT_NEEDED ||
    !dryingProjectOverview?.repairAction
  ) {
    if (dryingConclusionResult.downloadRefInternalPdfReport !== null) {
      status = vue.$t("projectLogs.projectState.interimReportCreated").toString();
    }
  }

  return labelAndStatus(
    joinToString([
      vue.$t(`projectLogs.projectState.dryingPackup${resourceKey}`).toString(),
      hasMultipleApartments 
      ? contactPersonNumber !== 0
        ? vue.$t("projectLogs.projectState.apartmentNumber", [contactPersonNumber]).toString()
        : vue.$t("projectLogs.projectState.allApartments").toString()
      : null,
    ], ""),
    !showOnlyLabel ? status : null
  );
}

function showProjectAcceptedStep(projectSteps: ProjectStepsContainer) {
  const projectAccepted = projectSteps.projectAccepted;
  const projectApproved = projectSteps.projectApproved;

  let showProjectAccepted = projectApproved === null;

  if (
    projectAccepted !== undefined && 
    projectApproved !== undefined &&
    projectAccepted !== null && 
    projectApproved !== null
  ) {
    const approvedTime = dateTimeFromIsoString(projectApproved.creationTimestamp).getTime();
    const acceptedTime = dateTimeFromIsoString(projectAccepted.creationTimestamp).getTime();
    const wasApprovedLater = Math.abs(approvedTime - acceptedTime) > (60 * 1000);

    if (wasApprovedLater) {
      showProjectAccepted = true;
    }
  }

  return showProjectAccepted;
}

function addProjectStep<T extends ProjectStepInfo>(
  step: T | null,
  timeOffsetInMillis: number | null = null,
  callback: (step: T) => string | null
) {
  if (step && step !== null) {
    const description = callback(step);
    if (!description) return;

    const creationTimestamp = getProjectStepCreationTimestamp(step);
    const createdBy = getProjectStepCreatedBy(step);

    const date = creationTimestamp 
      ? dateTimeFromIsoString(creationTimestamp)
      : null;

    if (date !== null) {
      const recordedAt =
        timeOffsetInMillis === null
          ? date
          : new Date(date.getTime() + timeOffsetInMillis);

      const recordInfo: RecordInfoFlex = {
        recordedAt: recordedAt,
        recordedByUserId: createdBy ?? "",
      };

      return filterOutUndefined({
        stepCreatedBy: recordInfo,
        stepDescription: description,
        stepSource: step.source
      }) as StepDescription;
    }
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function transformAppointmentStep(step: any) {
  return {
    ...step,
    createdBy: step.appointmentCreatedBy,
    creationTimestamp: step.appointmentCreationTimestamp,
    source: step.appointmentSource ?? step.source
  }
}

export function getStepDescriptionForLogEntries(
  vue: Vue,
  projectSteps: ProjectStepsContainer,
  projectOverview: DryingProjectOverview,
  project: ProjectDetails
) {
  const result: StepDescription[] = [];
  const hasMultipleApartments = project.clientData.contactPersons.length > 1;

  const projectTypeId = getProjectTypeIdOfProject(project);
  const hasLeakDetection = doesProjectTypeIdHaveLeakDetection(projectTypeId);
  const hasOnlyLeakDetection = isLeakDetectionOnlyProjectTypeId(projectTypeId);
  const hasDamageAssessment = doesProjectTypeIdHaveDamageAssessment(projectTypeId);
  const hasDrying = doesProjectTypeIdHaveDrying(projectTypeId);
  const hasRestoration = doesProjectTypeIdHaveRestoration(projectTypeId);

  const showProjectAccepted = showProjectAcceptedStep(projectSteps);

  if (showProjectAccepted) {
    const projectAcceptedStep = addProjectStep(
      projectSteps.projectAccepted,
      null,
      () =>
        getProjectAcceptedStepDescription(vue, undefined, true) ?? null
    );

    if (projectAcceptedStep) {
      result.push(projectAcceptedStep);
    }
  }

  const projectApproved = projectSteps.projectApproved;
  if (projectApproved) {
    const projectApprovedStep = addProjectStep(
      projectApproved,
      null,
      () => getProjectApprovedDescription(vue, projectApproved.orderAccepted, undefined, true) ?? null
    );
  
    if (projectApprovedStep) {
      result.push(projectApprovedStep);
    }
  }

  const firstContactStep = addProjectStep(
    projectSteps.firstContact,
    null,
    () => getFirstContactStepDescription(vue, undefined, true) ?? null
  );

  if (firstContactStep) {
    result.push(firstContactStep);
  }

  if (hasLeakDetection) {
    const leakDetectionAppointment = projectSteps.leakDetectionAppointment;
    if (leakDetectionAppointment) {
      if (
        leakDetectionAppointment.appointmentCreationTimestamp &&
        leakDetectionAppointment.appointmentCreationTimestamp !== "" &&
        leakDetectionAppointment.appointmentCreatedBy &&
        leakDetectionAppointment.appointmentCreatedBy !== ""
      ) {
        const leakDetectionAppointmentStep = addProjectStep(transformAppointmentStep(leakDetectionAppointment), null, () =>
          formatAppointmentInfo(
            vue,
            transformAppointmentStep(leakDetectionAppointment) as AppointmentInfo,
            AppointmentType.LEAK_DETECTION,
            hasMultipleApartments
          ) ?? null
        );
      
        if (leakDetectionAppointmentStep) {
          result.push(leakDetectionAppointmentStep);
        }
      }

      if (
        leakDetectionAppointment.creationTimestamp !== "" &&
        leakDetectionAppointment.createdBy !== ""
      ) {
        const leakDetectionPhaseBegun = addProjectStep(
          leakDetectionAppointment, 
          null, 
          () => getLeakDetectionPhaseBegunDescription(vue) ?? null
        );
      
        if (leakDetectionPhaseBegun) {
          result.push(leakDetectionPhaseBegun);
        }
      }
    }

    const leakDetectionResult = projectSteps.leakDetectionResult;
    if (leakDetectionResult) {
      const resourceKey = getResourceKey(leakDetectionResult);
      const leakDetectionResultStep = addProjectStep(
        leakDetectionResult,
        null,
        () => formatLeakDetectionResultStep(
          vue,
          leakDetectionResult as ProjectReportInfo,
          true,
          resourceKey
        ) ?? null
      );
    
      if (leakDetectionResultStep) {
        result.push(leakDetectionResultStep);
      }
    }
  }

  if (hasDamageAssessment) {
    const damageAssessmentAppointment = projectSteps.damageAssessmentAppointment;
    if (damageAssessmentAppointment) {
      if (
        damageAssessmentAppointment.appointmentCreationTimestamp &&
        damageAssessmentAppointment.appointmentCreationTimestamp !== "" &&
        damageAssessmentAppointment.appointmentCreatedBy &&
        damageAssessmentAppointment.appointmentCreatedBy !== ""
      ) {
        const damageAssessmentAppointmentStep = addProjectStep(transformAppointmentStep(damageAssessmentAppointment), null, () =>
          formatAppointmentInfo(
            vue,
            transformAppointmentStep(damageAssessmentAppointment) as AppointmentInfo,
            AppointmentType.DAMAGE_ASSESSMENT,
            hasMultipleApartments
          ) ?? null
        );
      
        if (damageAssessmentAppointmentStep) {
          result.push(damageAssessmentAppointmentStep);
        }
      }

      if (
        damageAssessmentAppointment.creationTimestamp !== "" &&
        damageAssessmentAppointment.createdBy !== ""
      ) {
        const damageAssessmentPhaseBegun = addProjectStep(
          damageAssessmentAppointment, 
          null, 
          () => getDamageAssessmentPhaseBegunDescription(vue) ?? null
        );
      
        if (damageAssessmentPhaseBegun) {
          result.push(damageAssessmentPhaseBegun);
        }
      }
    }

    const damageAssessmentResult = projectSteps.damageAssessmentResult;
    if (damageAssessmentResult) {
      const resourceKey = getResourceKey(damageAssessmentResult);
      const damageAssessmentResultStep = addProjectStep(
        damageAssessmentResult,
        null,
        () =>
          formatDamageAssessmentResultStepForDryingStep(
            vue,
            damageAssessmentResult as DamageAssessmentResultStep,
            project,
            true,
            hasDrying,
            resourceKey
          ) ?? null
      );

      if (damageAssessmentResultStep) {
        result.push(damageAssessmentResultStep);
      }
    }

    const offerSentToInsuranceStep = addProjectStep(
      projectSteps.offerSendToInsurance,
      null,
      () =>
        getOfferSendToInsuranceDescription(vue) ?? null
    );

    if (offerSentToInsuranceStep) {
      result.push(offerSentToInsuranceStep);
    }
  }

  if (hasDrying) {
    projectSteps.dryingSetupAppointments?.forEach((appointment) => {
      if (
        appointment.appointmentCreationTimestamp &&
        appointment.appointmentCreationTimestamp !== "" &&
        appointment.appointmentCreatedBy &&
        appointment.appointmentCreatedBy !== ""
      ) {
        const dryingSetupAppointmentStep = addProjectStep(transformAppointmentStep(appointment), null, () =>
          formatAppointmentInfo(
            vue,
            transformAppointmentStep(appointment) as AppointmentInfo,
            AppointmentType.PREPARE_DRYING,
            hasMultipleApartments
          ) ?? null
        );
      
        if (dryingSetupAppointmentStep) {
          result.push(dryingSetupAppointmentStep);
        }
      }

      if (
        appointment.creationTimestamp !== "" &&
        appointment.createdBy !== ""
      ) {
        const dryingSetupPhaseBegun = addProjectStep(
          appointment, 
          null, 
          () => getDryingSetupPhaseBegunDescription(
            vue, 
            appointment.contactPersonNumber,
            hasMultipleApartments
          ) ?? null
        );
      
        if (dryingSetupPhaseBegun) {
          result.push(dryingSetupPhaseBegun);
        }
      }
    });

    const dryingSetupCompletions = projectSteps.dryingSetupCompletions;
    
    if (dryingSetupCompletions) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dryingSetupCompletions.forEach((dryingSetupCompletion: any) => {
        const resourceKey = getResourceKey(dryingSetupCompletion);
        const dryingSetupCompletionStep = addProjectStep(
          dryingSetupCompletion,
          null,
          () =>
            getDryingPreparationResult(
              vue,
              dryingSetupCompletion.contactPersonNumber,
              hasMultipleApartments,
              getEstimatedDryingTimeInDays(projectSteps),
              true,
              resourceKey
            ) ?? null
        );
    
        if (dryingSetupCompletionStep) {
          result.push(dryingSetupCompletionStep);
        }
      }); 
    }

    projectSteps.intermediateMeasurementAppointments?.forEach((appointment) => {
      if (
        appointment.appointmentCreationTimestamp &&
        appointment.appointmentCreationTimestamp !== "" &&
        appointment.appointmentCreatedBy &&
        appointment.appointmentCreatedBy !== ""
      ) {
        const intermediateMeasurementAppointmentStep = addProjectStep(transformAppointmentStep(appointment), null, () =>
          formatAppointmentInfo(
            vue,
            transformAppointmentStep(appointment) as AppointmentInfo,
            AppointmentType.INTERMEDIATE_MEASUREMENT,
            hasMultipleApartments
          ) ?? null
        );
      
        if (intermediateMeasurementAppointmentStep) {
          result.push(intermediateMeasurementAppointmentStep);
        }
      }

      if (
        appointment.creationTimestamp !== "" &&
        appointment.createdBy !== ""
      ) {
        const intermediateMeasurementPhaseBegun = addProjectStep(
          appointment, 
          null, 
          () => getIntermediateMeasurementPhaseBegunDescription(
            vue, 
            appointment.contactPersonNumber,
            hasMultipleApartments
          ) ?? null
        );
      
        if (intermediateMeasurementPhaseBegun) {
          result.push(intermediateMeasurementPhaseBegun);
        }
      }
    });

    const intermediateMeasurementResults = projectSteps.intermediateMeasurementCompletions;
    if (intermediateMeasurementResults) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      intermediateMeasurementResults.forEach((intermediateMeasurementResult: any) => {
        const resourceKey = getResourceKey(intermediateMeasurementResult);
        const intermediateMeasurementResultStep = addProjectStep(
          intermediateMeasurementResult,
          null,
          () =>
            getIntermediateMeasurementResultDescription(
              vue,
              intermediateMeasurementResult.contactPersonNumber,
              hasMultipleApartments,
              resourceKey
            ) ?? null
        );
    
        if (intermediateMeasurementResultStep) {
          result.push(intermediateMeasurementResultStep);
        }
      }); 
    }

    projectSteps.dryingConclusionAppointments?.forEach((appointment) => {
      if (
        appointment.appointmentCreationTimestamp &&
        appointment.appointmentCreationTimestamp !== "" &&
        appointment.appointmentCreatedBy &&
        appointment.appointmentCreatedBy !== ""
      ) {
        const dryingConclusionAppointmentStep = addProjectStep(transformAppointmentStep(appointment), null, () =>
          formatAppointmentInfo(
            vue,
            transformAppointmentStep(appointment) as AppointmentInfo,
            AppointmentType.DRYING_CONCLUSION,
            hasMultipleApartments
          ) ?? null
        );
      
        if (dryingConclusionAppointmentStep) {
          result.push(dryingConclusionAppointmentStep);
        }
      }

      if (
        appointment.creationTimestamp !== "" &&
        appointment.createdBy !== ""
      ) {
        const dryingPackupPhaseBegun = addProjectStep(
          appointment, 
          null, 
          () => getDryingPackupBegunDescription(
            vue, 
            appointment.contactPersonNumber,
            hasMultipleApartments
          ) ?? null
        );
      
        if (dryingPackupPhaseBegun) {
          result.push(dryingPackupPhaseBegun);
        }
      }
    });

    const dryingConclusionResults = projectSteps.dryingConclusionResults;
    if (dryingConclusionResults) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dryingConclusionResults.forEach((dryingConclusionResult: any) => {
        const resourceKey = getResourceKey(dryingConclusionResult);
        const dryingConclusionStep = addProjectStep(
          dryingConclusionResult,
          null,
          () =>
            getDryingConclusionResultDescription(
              vue,
              dryingConclusionResult as DryingConclusionResult,
              projectOverview,
              dryingConclusionResult.contactPersonNumber,
              hasMultipleApartments,
              true,
              resourceKey
            ) ?? null
        );
    
        if (dryingConclusionStep) {
          result.push(dryingConclusionStep);
        }
      }); 
    }
  }

  if (hasRestoration) {
    projectSteps.repairAppointments?.forEach((appointment) => {
      if (
        appointment.appointmentCreationTimestamp &&
        appointment.appointmentCreationTimestamp !== "" &&
        appointment.appointmentCreatedBy &&
        appointment.appointmentCreatedBy !== ""
      ) {
        const repairAppointmentStep = addProjectStep(transformAppointmentStep(appointment), null, () =>
          formatAppointmentInfo(
            vue,
            transformAppointmentStep(appointment) as AppointmentInfo,
            AppointmentType.REPAIR,
            hasMultipleApartments
          ) ?? null
        );
      
        if (repairAppointmentStep) {
          result.push(repairAppointmentStep);
        }
      }

      if (
        appointment.creationTimestamp !== "" &&
        appointment.createdBy !== ""
      ) {
        const repairPhaseBegun = addProjectStep(
          appointment, 
          null, 
          () => getRepairBegunDescription(
            vue, 
            appointment.contactPersonNumber,
            hasMultipleApartments
          ) ?? null
        );
      
        if (repairPhaseBegun) {
          result.push(repairPhaseBegun);
        }
      }
    });

    const repairConclusionResults = projectSteps.repairStart?.extendedRepairResult ?? [projectSteps.repairStart];
    const repairCompletedBy = projectOverview.repairCompletedBy
      ?? project.tradesData?.conclusionData?.confirmedBy;
    
    if (repairConclusionResults) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      repairConclusionResults.forEach((repairConclusionResult: any) => {
        const resourceKey = getResourceKey(repairConclusionResult);
        const repairCompletionStep = addProjectStep(
          repairConclusionResult,
          null,
          () =>
            getRepairResult(
              vue,
              repairConclusionResult.contactPersonNumber,
              hasMultipleApartments,
              resourceKey
            ) ?? null
        );
    
        if (repairCompletionStep) {
          result.push(repairCompletionStep);
        }
      }); 
    }
    else if (repairCompletedBy && repairCompletedBy !== null) {
      const repairCompletionStep = addProjectStep(
        {
          createdBy: repairCompletedBy.recordedByUserId,
          creationTimestamp: repairCompletedBy.recordedAt.toDate().toISOString(),
        },
        null,
        () =>
          getRepairConcludedDescription(vue) ?? null
      );

      if (repairCompletionStep) {
        result.push(repairCompletionStep);
      }
    }
  }

  if (!hasOnlyLeakDetection) {
    const projectResult = projectSteps.projectResult;
    if (projectResult) {
      const projectResultStep = addProjectStep(
        projectResult,
        null,
        () => getProjectResultDescription(vue, projectResult, true) ?? null
      );
  
      if (projectResultStep) {
        result.push(projectResultStep);
      }
    }
  }
    
  return result;
}
